import { spring, interpolate } from 'remotion';
import { THEME, getScale, getLayoutMode } from '../../themes/brand';
import { getIcon } from './iconMap';
import { BarChart3 } from 'lucide-react';
import type { BrollComponentProps } from './registry';

interface KpiItem {
  label: string;
  value?: number;
  text?: string;
  suffix?: string;
  color?: string;
  icon?: string;
  target?: number;
}

interface DashboardProps {
  title?: string;
  icon?: string;
  kpis?: KpiItem[];
}

export const TemplateDashboard: React.FC<BrollComponentProps> = ({
  frame, fps, width, height, durationInFrames, props,
}) => {
  const s = getScale(height);
  const mode = getLayoutMode(height);
  const data = (props || {}) as DashboardProps;
  const dur = durationInFrames;

  const title = data.title || 'Dashboard';
  const TitleIcon = getIcon(data.icon) || BarChart3;
  const kpis: KpiItem[] = data.kpis || [
    { label: 'Metric 1', value: 1234, icon: 'TrendingUp', color: THEME.colors.accent },
    { label: 'Metric 2', value: 85, suffix: '%', icon: 'Users', color: THEME.colors.success },
    { label: 'Metric 3', value: 42, icon: 'Zap', color: THEME.colors.warning },
    { label: 'Metric 4', text: 'Active', icon: 'CheckCircle', color: THEME.colors.accentLight },
  ];

  const titleEntrance = spring({ frame, fps, config: { damping: 18 }, durationInFrames: Math.max(10, Math.round(dur * 0.15)) });
  const visibleKpis = mode === 'compact' ? kpis.slice(0, 4) : kpis.slice(0, 6);
  const cols = mode === 'full' && visibleKpis.length > 4 ? 3 : 2;
  const cardWidth = (width - THEME.spacing.xl * s * (cols + 1)) / cols;
  const staggerGap = Math.max(2, Math.floor((dur * 0.4) / visibleKpis.length));

  return (
    <div style={{
      width: '100%', height: '100%',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      backgroundColor: THEME.colors.bg, fontFamily: THEME.font.family,
      padding: THEME.spacing.xl * s,
    }}>
      {/* Title with icon-in-pill */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: THEME.spacing.md * s,
        marginBottom: THEME.spacing.xl * s,
        opacity: titleEntrance,
        transform: `translateY(${interpolate(titleEntrance, [0, 1], [20, 0])}px)`,
      }}>
        <div style={{
          width: 44 * s, height: 44 * s,
          backgroundColor: THEME.colors.bgSecondary,
          borderRadius: THEME.radius.md * s,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `1px solid ${THEME.colors.border}`,
        }}>
          <TitleIcon size={22 * s} color={THEME.colors.accent} />
        </div>
        <div style={{
          color: THEME.colors.text, fontSize: THEME.font.title * s,
          fontWeight: THEME.font.weight.bold, letterSpacing: 2 * s,
        }}>
          {title}
        </div>
      </div>

      {/* KPI Grid */}
      <div style={{
        display: 'flex', flexWrap: 'wrap',
        gap: THEME.spacing.lg * s, justifyContent: 'center',
        maxWidth: (cardWidth + THEME.spacing.lg * s) * cols,
      }}>
        {visibleKpis.map((kpi, i) => {
          const delay = i * staggerGap;
          const entrance = spring({
            frame: Math.max(0, frame - delay), fps,
            config: { damping: 12, stiffness: 80 },
          });
          const cardColor = kpi.color || THEME.colors.chart[i % THEME.colors.chart.length];
          const KpiIcon = getIcon(kpi.icon);

          let displayValue = kpi.text || '';
          if (kpi.value !== undefined) {
            const countStart = delay + Math.round(dur * 0.1);
            const countEnd = Math.round(dur * 0.7);
            const num = Math.floor(interpolate(frame, [countStart, countEnd], [0, kpi.value], {
              extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
            }));
            displayValue = num.toLocaleString() + (kpi.suffix || '');
          }

          // Progress bar (fraction of target or 100)
          const target = kpi.target || (kpi.suffix === '%' ? 100 : (kpi.value || 100));
          const progressPct = kpi.value !== undefined ? Math.min(100, (kpi.value / target) * 100) : 0;
          const progressStart = delay + Math.round(dur * 0.15);
          const progressEnd = Math.round(dur * 0.75);
          const progressAnim = interpolate(frame, [progressStart, progressEnd], [0, progressPct], {
            extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
          });

          return (
            <div key={kpi.label} style={{
              width: cardWidth,
              backgroundColor: THEME.colors.bgCard,
              borderRadius: THEME.radius.lg * s,
              padding: `${THEME.spacing.lg * s}px`,
              border: `1px solid ${THEME.colors.border}`,
              opacity: entrance,
              transform: `translateY(${interpolate(entrance, [0, 1], [30, 0])}px) scale(${interpolate(entrance, [0, 1], [0.92, 1])})`,
            }}>
              {/* Icon-in-pill + label */}
              <div style={{ display: 'flex', alignItems: 'center', gap: THEME.spacing.sm * s, marginBottom: THEME.spacing.md * s }}>
                {KpiIcon && (
                  <div style={{
                    width: 32 * s, height: 32 * s,
                    backgroundColor: THEME.colors.bgSecondary,
                    borderRadius: THEME.radius.sm * s,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: `1px solid ${THEME.colors.border}`,
                  }}>
                    <KpiIcon size={16 * s} color={cardColor} />
                  </div>
                )}
                <div style={{
                  color: THEME.colors.textMuted, fontSize: THEME.font.label * s,
                  fontWeight: THEME.font.weight.medium, letterSpacing: 0.5,
                }}>
                  {kpi.label}
                </div>
              </div>

              {/* Value */}
              <div style={{
                color: THEME.colors.text,
                fontSize: (kpi.value !== undefined ? THEME.font.title * 1.2 : THEME.font.title) * s,
                fontWeight: THEME.font.weight.black, lineHeight: 1.1,
                marginBottom: kpi.value !== undefined ? THEME.spacing.md * s : 0,
              }}>
                {displayValue}
              </div>

              {/* Gradient progress bar */}
              {kpi.value !== undefined && (
                <div style={{
                  width: '100%', height: 4 * s,
                  backgroundColor: THEME.colors.bgSecondary,
                  borderRadius: THEME.radius.pill,
                  overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${progressAnim}%`, height: '100%',
                    background: `linear-gradient(90deg, ${THEME.colors.accent}, ${THEME.colors.accentHover})`,
                    borderRadius: THEME.radius.pill,
                  }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
